import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { downloadXlsx, dateStamp } from '@/lib/download-xlsx';
import { showErrorToast } from '@/hooks/use-mutation-with-toast';
import {
  MizanPeriodPicker,
  defaultMizanPeriod,
  mizanPeriodToDates,
} from '@/components/reports/mizan-period-picker';

const selectClass =
  'h-8 border border-border bg-background px-2.5 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20';

// Fallback while the registry loads; the live list comes from tradingCariType.
const FALLBACK_CARI_TYPES = ['M', 'T', 'S', 'B', 'ORT', 'P', 'AH', 'E', 'X', 'I'];
const CURRENCIES = ['EUR', 'GBP', 'USD', 'TRY'] as const;
const CURRENCY_NAMES: Record<string, string> = {
  TRY: 'TÜRK LİRASI - TL',
  EUR: 'EURO - EUR',
  GBP: 'İNGİLİZ STERLİNİ - GBP',
  USD: 'AMERİKAN DOLARI - USD',
};

// Legacy ProAcenta "Cari Mizan": per-counterparty four-column trial balance
// (Borç/Alacak turnover + Borç Bakiye/Alacak Bakiye) grouped by currency and
// cari type, with kümülatif / aylık / tarih aralığı date modes.
export function CariMizanPage() {
  const { t, i18n } = useTranslation('trading');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';

  const [period, setPeriod] = useState(defaultMizanPeriod());
  const [cariTypes, setCariTypes] = useState<string[]>([]);
  const [currency, setCurrency] = useState('');
  const [includeZero, setIncludeZero] = useState(true);
  const [excludeCarryForward, setExcludeCarryForward] = useState(false);
  const [valueMode, setValueMode] = useState<'native' | 'tryEquivalent'>('native');
  const [sortBy, setSortBy] = useState<'code' | 'name'>('code');
  const [exporting, setExporting] = useState(false);

  const { start, end } = useMemo(() => mizanPeriodToDates(period), [period]);

  const queryInput = {
    startDate: start,
    endDate: end,
    cariTypes: cariTypes.length > 0 ? cariTypes : undefined,
    currency: (currency || undefined) as (typeof CURRENCIES)[number] | undefined,
    includeZeroBalances: includeZero,
    excludeOpeningCarryForward: excludeCarryForward,
    valueMode,
    sortBy,
  };

  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.tradingCari.cariMizan.useQuery(queryInput);
  const { data: cariTypeRows } = trpc.tradingCariType.list.useQuery();
  const cariTypeCodes = cariTypeRows?.map((tp) => tp.code) ?? FALLBACK_CARI_TYPES;

  const onExport = async () => {
    setExporting(true);
    try {
      const res = await utils.tradingCari.exportCariMizan.fetch(queryInput);
      downloadXlsx(res.rows, 'Cari Mizan', `cari-mizan-${dateStamp()}.xlsx`);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const toggleType = (ct: string) => {
    setCariTypes((prev) => (prev.includes(ct) ? prev.filter((x) => x !== ct) : [...prev, ct]));
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  const fmtCell = (n: number) => (Math.abs(n) < 0.005 ? '—' : fmt(n));

  const typeLabel = (ct: string) => t(`cari.${(
    { M: 'customer', T: 'supplier', S: 'vendor', B: 'dealer', ORT: 'partner', P: 'personnel', AH: 'openAccount', E: 'government', X: 'legal', I: 'insurance' } as Record<string, string>
  )[ct] ?? 'customer'}`);

  const fmtDay = (d: Date | string) => new Date(d).toLocaleDateString(dateLocale);

  const periodLabel = data
    ? data.startDate
      ? `${fmtDay(data.startDate)} - ${fmtDay(data.endDate)}`
      : t('cariMizan.asOfValue', 'As of {{date}}', { date: fmtDay(data.endDate) })
    : '';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('cariMizan.title', 'Cari Mizan')}</h1>
          <p className="text-muted-foreground">
            {t('cariMizan.subtitle', 'Per-account trial balance: turnover and balance per currency')}
          </p>
        </div>
        <Button variant="outline" onClick={onExport} disabled={exporting || !data?.currencies.length}>
          {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          {t('cariMizan.export', 'Export')}
        </Button>
      </div>

      {/* Filters */}
      <div className="space-y-4 rounded-lg border bg-card p-4">
        {/* Mizan şekli */}
        <MizanPeriodPicker value={period} onChange={setPeriod} ns="trading" keyPrefix="cariMizan" />

        {/* Cari type chips */}
        <div className="space-y-1">
          <Label className="text-xs">
            {t('cariMizan.cariTypes', 'Cari Types')}{' '}
            <span className="text-muted-foreground">({t('cariMizan.allWhenEmpty', 'all when none selected')})</span>
          </Label>
          <div className="flex flex-wrap gap-1">
            {cariTypeCodes.map((ct) => (
              <button
                key={ct}
                type="button"
                onClick={() => toggleType(ct)}
                className={`h-7 border px-2 text-xs transition-colors ${
                  cariTypes.includes(ct)
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background hover:bg-muted'
                }`}
                aria-pressed={cariTypes.includes(ct)}
              >
                {ct} · {typeLabel(ct)}
              </button>
            ))}
          </div>
        </div>

        {/* Value ranges + report type */}
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label className="text-xs">{t('cariMizan.currency', 'Currency')}</Label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={`${selectClass} block w-28`}>
              <option value="">{t('cariMizan.allCurrencies', 'All')}</option>
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('cariMizan.reportType', 'Report Type')}</Label>
            <select
              value={valueMode}
              onChange={(e) => setValueMode(e.target.value as 'native' | 'tryEquivalent')}
              className={`${selectClass} block w-64`}
            >
              <option value="native">{t('cariMizan.nativeMode', 'FX and/or TL mizan')}</option>
              <option value="tryEquivalent">{t('cariMizan.tryMode', 'TL equivalents of FX transactions')}</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('cariMizan.sort', 'Sort')}</Label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'code' | 'name')} className={`${selectClass} block w-40`}>
              <option value="code">{t('cariMizan.sortByCode', 'By cari no')}</option>
              <option value="name">{t('cariMizan.sortByName', 'By name')}</option>
            </select>
          </div>
          <div className="flex items-center gap-2 pb-1.5">
            <input
              type="checkbox"
              id="includeZeroMizan"
              checked={includeZero}
              onChange={(e) => setIncludeZero(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="includeZeroMizan">{t('cariMizan.includeZero', 'Include zero balances')}</Label>
          </div>
          <div className="flex items-center gap-2 pb-1.5">
            <input
              type="checkbox"
              id="excludeCarryForward"
              checked={excludeCarryForward}
              onChange={(e) => setExcludeCarryForward(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="excludeCarryForward">
              {t('cariMizan.excludeCarryForward', 'Exclude prior year-end carryover')}
            </Label>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : data ? (
        <div className="rounded-xl border bg-card">
          <div className="border-b p-4">
            <h3 className="text-lg font-semibold">
              {period.mode === 'range'
                ? t('cariMizan.rangeTitle', 'Date Range Cari Mizan')
                : t('cariMizan.title', 'Cari Mizan')}
            </h3>
            <p className="text-sm text-muted-foreground">{periodLabel}</p>
          </div>
          {data.currencies.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground">{t('cariMizan.noResults', 'No movements in this period')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-3 text-left font-medium">#</th>
                    <th className="px-3 py-3 text-left font-medium">{t('cariMizan.cariNo', 'Cari No')}</th>
                    <th className="px-3 py-3 text-left font-medium">{t('cariMizan.name', 'Name')}</th>
                    <th className="px-3 py-3 text-right font-medium">{t('cariMizan.debit', 'Debit (Borç)')}</th>
                    <th className="px-3 py-3 text-right font-medium">{t('cariMizan.credit', 'Credit (Alacak)')}</th>
                    <th className="px-3 py-3 text-right font-medium">{t('cariMizan.debitBalance', 'Debit Balance')}</th>
                    <th className="px-3 py-3 text-right font-medium">{t('cariMizan.creditBalance', 'Credit Balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.currencies.map((cur) => (
                    <CurrencySection
                      key={cur.currency}
                      section={cur}
                      typeLabel={typeLabel}
                      fmt={fmt}
                      fmtCell={fmtCell}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// Structural mirror of tradingCari.cariMizan's response (see packages/api
// routers/tradingCari.ts computeCariMizan) — the query result is assignable.
interface MizanRowData {
  source: 'customer' | 'supplier';
  id: string;
  cariNo: string;
  companyName: string;
  cariType: string;
  debit: number;
  credit: number;
  debitBalance: number;
  creditBalance: number;
}

interface MizanTotalsData {
  debit: number;
  credit: number;
  debitBalance: number;
  creditBalance: number;
  net: number;
}

interface CariGroupData {
  cariType: string;
  rows: MizanRowData[];
  totals: MizanTotalsData;
}

interface CurrencySectionData {
  currency: string;
  cariGroups: CariGroupData[];
  totals: MizanTotalsData;
}

interface CurrencySectionProps {
  section: CurrencySectionData;
  typeLabel: (ct: string) => string;
  fmt: (n: number) => string;
  fmtCell: (n: number) => string;
}

function CurrencySection({ section, typeLabel, fmt, fmtCell }: CurrencySectionProps) {
  const { t } = useTranslation('trading');
  let rowNo = 0;
  return (
    <>
      <tr className="border-y bg-muted/70">
        <td colSpan={7} className="px-3 py-2 font-semibold">
          {CURRENCY_NAMES[section.currency] ?? section.currency}
        </td>
      </tr>
      {section.cariGroups.map((group) => (
        <CariGroupRows
          key={group.cariType}
          group={group}
          startNo={(() => {
            const s = rowNo;
            rowNo += group.rows.length;
            return s;
          })()}
          typeLabel={typeLabel}
          fmtCell={fmtCell}
          fmt={fmt}
        />
      ))}
      <tr className="border-t-2 bg-muted/50 font-bold">
        <td colSpan={3} className="px-3 py-2.5">
          {section.currency} {t('cariMizan.total', 'TOTAL')}
        </td>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmt(section.totals.debit)}</td>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmt(section.totals.credit)}</td>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmt(section.totals.debitBalance)}</td>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmt(section.totals.creditBalance)}</td>
      </tr>
      <tr className="border-b bg-muted/30 font-semibold">
        <td colSpan={5} />
        <td colSpan={2} className={`px-3 py-2 text-right ${section.totals.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {t('cariMizan.balance', 'BALANCE')}: {fmt(Math.abs(section.totals.net))}{' '}
          {section.totals.net >= 0 ? t('cariMizan.debitShort', '(B)') : t('cariMizan.creditShort', '(A)')}
        </td>
      </tr>
    </>
  );
}

interface CariGroupRowsProps {
  group: CariGroupData;
  startNo: number;
  typeLabel: (ct: string) => string;
  fmt: (n: number) => string;
  fmtCell: (n: number) => string;
}

function CariGroupRows({ group, startNo, typeLabel, fmt, fmtCell }: CariGroupRowsProps) {
  const { t } = useTranslation('trading');
  return (
    <>
      <tr className="border-b bg-muted/40">
        <td colSpan={7} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {group.cariType} - {typeLabel(group.cariType)}
        </td>
      </tr>
      {group.rows.map((row, i) => (
        <tr key={`${row.source}-${row.id}`} className="border-b last:border-0 hover:bg-muted/30">
          <td className="px-3 py-2 text-muted-foreground">{startNo + i + 1}</td>
          <td className="px-3 py-2 font-mono text-xs">{row.cariNo || '—'}</td>
          <td className="px-3 py-2">{row.companyName}</td>
          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtCell(row.debit)}</td>
          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtCell(row.credit)}</td>
          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtCell(row.debitBalance)}</td>
          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtCell(row.creditBalance)}</td>
        </tr>
      ))}
      <tr className="border-b bg-muted/20 font-semibold">
        <td colSpan={3} className="px-3 py-2">
          {group.cariType} - {typeLabel(group.cariType)} {t('cariMizan.total', 'TOTAL')}
        </td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(group.totals.debit)}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(group.totals.credit)}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(group.totals.debitBalance)}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(group.totals.creditBalance)}</td>
      </tr>
    </>
  );
}
